/**
 * Integrytas Group - Base App (MOBILE)
 * Page: FLN.FIELD_SOURCE — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/FlnFieldSourcePage.tsx (the
 * field-lineage / data-provenance worklist). A DISTINCT presentation (KPI tiles +
 * dense card list + status facet chips + bottom-sheet detail + edit sheet) over
 * the SAME data/logic layer only:
 *   - reads via apiDb (fln_field_source + the fln_source_kind / fln_resolution_status
 *     _tl label tables + csm_page_master _tl for page-name resolution), resolving
 *     every code to its translated label against the ACTIVE locale (§8, §17.B4 — no
 *     raw id/code reaches render),
 *   - the field-source mapping write (source table.column + kind, which re-derives
 *     the resolution status and locks the row with is_manual='Y') routes through the
 *     Common Routine via routedUpdate — §2 single write path; never a direct write.
 * It never re-renders the desktop grid; §16. The desktop still owns the multilingual
 * description editing and the source re-scan, so descriptions are shown read-only
 * here (mirrors MobileUserEditSheet's read-only roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, SquarePen, Database } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'FLN.FIELD_SOURCE';

// Map the internal lead/pill class strings onto the Direction-C primitive tones.
const LEAD_TONE_KEY: Record<string, MobileLeadTone> = {
  'm-l-green': 'green', 'm-l-blue': 'blue', 'm-l-amber': 'amber', 'm-l-red': 'red', 'm-l-slate': 'slate',
};
const PILL_TONE_KEY: Record<string, MobilePillTone> = {
  'm-p-ok': 'ok', 'm-p-info': 'info', 'm-p-wait': 'wait', 'm-p-no': 'no', 'm-p-you': 'you',
};

// Resolution-status code → mobile status-pill + lead-icon tint. Labels come
// localized from the fln_resolution_status_tl lookup, never these keys.
const STATUS_PILL: Record<string, string> = {
  RESOLVED: 'm-p-ok',
  NEEDS_DESCRIPTION: 'm-p-wait',
  UNRESOLVED: 'm-p-no',
};
const STATUS_LEAD: Record<string, string> = {
  RESOLVED: 'm-l-green',
  NEEDS_DESCRIPTION: 'm-l-amber',
  UNRESOLVED: 'm-l-red',
};

interface FlnRow {
  fln_field_source_id: number;
  page_code: string;
  component_key: string;
  field_key: string;
  label: string | null;
  source_table: string | null;
  source_column: string | null;
  data_type: string | null;
  is_custom: 'Y' | 'N';
  source_kind_code: string;
  resolution_status_code: string;
  is_manual: 'Y' | 'N';
  tech_hint: string | null;
}

type LabelMap = Record<string, string>;
type StatusFacet = 'all' | 'RESOLVED' | 'NEEDS_DESCRIPTION' | 'UNRESOLVED';

interface TlLabelRow { code: string; locale_code: string; display_text: string }
interface PageTlRow { locale_code: string; display_text: string }
interface RawPageRow { internal_code: string; csm_page_master_tl?: PageTlRow[] }

export function MobileFlnFieldSourcePage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<FlnRow[]>([]);
  const [kindLabels, setKindLabels] = useState<LabelMap>({});
  const [statusLabels, setStatusLabels] = useState<LabelMap>({});
  const [pageNames, setPageNames] = useState<LabelMap>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<FlnRow | null>(null);
  const [editing, setEditing] = useState<FlnRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve a lookup _tl map for the active locale (EN_US fallback) — §8/§17.B4.
  const buildLabelMap = (data: TlLabelRow[]): LabelMap => {
    const m: LabelMap = {};
    for (const r of data) if (r.locale_code === 'EN_US') m[r.code] = r.display_text;
    for (const r of data) if (r.locale_code === activeLocale) m[r.code] = r.display_text;
    return m;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [fields, kinds, statuses, pages] = await Promise.all([
        apiDb
          .from('fln_field_source')
          .select('fln_field_source_id, page_code, component_key, field_key, label, source_table, source_column, data_type, is_custom, source_kind_code, resolution_status_code, is_manual, tech_hint')
          .order('page_code')
          .order('field_key'),
        apiDb.from('fln_source_kind_tl').select('code, locale_code, display_text'),
        apiDb.from('fln_resolution_status_tl').select('code, locale_code, display_text'),
        apiDb.from('csm_page_master').select('internal_code, csm_page_master_tl ( locale_code, display_text )'),
      ]);
      if (fields.error) throw fields.error;
      setRows((fields.data ?? []) as FlnRow[]);
      setKindLabels(buildLabelMap((kinds.data ?? []) as TlLabelRow[]));
      setStatusLabels(buildLabelMap((statuses.data ?? []) as TlLabelRow[]));

      const names: LabelMap = {};
      for (const p of (pages.data ?? []) as RawPageRow[]) {
        const tls = p.csm_page_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        names[p.internal_code] = exact?.display_text ?? fb?.display_text ?? p.internal_code;
      }
      setPageNames(names);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (labels follow it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const pageName = (code: string) => pageNames[code] ?? code;
  const kindLabel = (code: string) => kindLabels[code] ?? code;
  const statusLabel = (code: string) => statusLabels[code] ?? code;

  const sourceText = (r: FlnRow): string | null =>
    r.source_table && r.source_column ? `${r.source_table}.${r.source_column}` : null;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet !== 'all' && r.resolution_status_code !== statusFacet) return false;
      if (!q) return true;
      return (
        r.field_key.toLowerCase().includes(q) ||
        (r.label ?? '').toLowerCase().includes(q) ||
        r.page_code.toLowerCase().includes(q) ||
        (r.source_table ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  const totalCount = rows.length;
  const resolvedCount = rows.filter((r) => r.resolution_status_code === 'RESOLVED').length;
  const attentionCount = rows.filter((r) => r.resolution_status_code !== 'RESOLVED').length;

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.fln.all', 'All') },
    { key: 'RESOLVED', label: statusLabel('RESOLVED') || t('mobile.fln.resolved', 'Resolved') },
    { key: 'NEEDS_DESCRIPTION', label: statusLabel('NEEDS_DESCRIPTION') || t('mobile.fln.needs_desc', 'Needs description') },
    { key: 'UNRESOLVED', label: statusLabel('UNRESOLVED') || t('mobile.fln.unresolved', 'Unresolved') },
  ];

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.fln.title', 'Field Sources')}
        description={t('mobile.fln.description', 'Data provenance for every displayed field and its resolution status.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.fln.kpi_total', 'Fields')}</div>
          <div className="m-kpi-v">{totalCount.toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.fln.kpi_resolved', 'Resolved')}</div>
          <div className="m-kpi-v">{resolvedCount.toLocaleString()}</div>
        </div>
        <div className={'m-kpi' + (attentionCount > 0 ? ' m-kpi-alert' : '')}>
          <div className="m-kpi-l">{t('mobile.fln.kpi_attention', 'Needs attention')}</div>
          <div className="m-kpi-v">{attentionCount.toLocaleString()}</div>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.fln.search', 'Search field, label, page or table')}
          aria-label={t('mobile.fln.search', 'Search field, label, page or table')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.fln.none', 'No fields match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const src = sourceText(r);
            return (
              <MobileCardRow
                key={r.fln_field_source_id}
                lead={<Database />}
                leadTone={LEAD_TONE_KEY[STATUS_LEAD[r.resolution_status_code] ?? 'm-l-slate']}
                title={r.label || r.field_key}
                subtitle={`${pageName(r.page_code)} · ${src ?? t('mobile.fln.unmapped', 'unmapped')}`}
                trailing={
                  <MobileStatusPill tone={PILL_TONE_KEY[STATUS_PILL[r.resolution_status_code] ?? 'm-p-info']}>
                    {statusLabel(r.resolution_status_code)}
                  </MobileStatusPill>
                }
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.fln.detail', 'Field source detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{selected.label || selected.field_key}</div>
                <div className="m-em">{selected.field_key}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.fln.status', 'Status')}
                value={
                  <MobileStatusPill tone={PILL_TONE_KEY[STATUS_PILL[selected.resolution_status_code] ?? 'm-p-info']}>
                    {statusLabel(selected.resolution_status_code)}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.fln.page', 'Page / component')}
                value={`${pageName(selected.page_code)}${selected.component_key ? ' · ' + selected.component_key : ''}`}
              />
              <MobileDetailRow label={t('mobile.fln.source', 'Source')} value={sourceText(selected) ?? t('mobile.fln.unmapped', 'unmapped')} />
              <MobileDetailRow label={t('mobile.fln.kind', 'Source kind')} value={kindLabel(selected.source_kind_code)} />
              <MobileDetailRow label={t('mobile.fln.data_type', 'Data type')} value={selected.data_type || t('common.none', 'None')} />
              <MobileDetailRow
                label={t('mobile.fln.custom', 'Custom field')}
                value={
                  <MobileStatusPill tone={selected.is_custom === 'Y' ? 'info' : 'no'}>
                    {selected.is_custom === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              {selected.tech_hint && (
                <MobileDetailRow label={t('mobile.fln.detected', 'Detected')} value={selected.tech_hint} />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.fln.map', 'Map source')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileFlnEditSheet
        session={session}
        row={editing}
        activeLocale={activeLocale}
        kindLabels={kindLabels}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — maps the field to its source table.column + kind, saved through
// the Common Routine (routedUpdate) — §2. The routine re-derives the resolution
// status (RESOLVED / NEEDS_DESCRIPTION / UNRESOLVED) exactly like the desktop and
// locks the row with is_manual='Y'. The multilingual descriptions live in the
// fln_field_source_tl table and their editing stays on the desktop, so the active
// locale's description is shown read-only here (mirrors MobileUserEditSheet).
// ---------------------------------------------------------------------------

interface MobileFlnEditSheetProps {
  session: AuthSession;
  row: FlnRow | null;
  activeLocale: string;
  kindLabels: LabelMap;
  onClose: () => void;
  onSaved: () => void;
}

interface FlnTlRow { locale_code: string; description: string | null; derivation_notes: string | null }

function MobileFlnEditSheet({ session, row, activeLocale, kindLabels, onClose, onSaved }: MobileFlnEditSheetProps) {
  const { t } = useTranslation();
  const [sourceTable, setSourceTable] = useState('');
  const [sourceColumn, setSourceColumn] = useState('');
  const [kindCode, setKindCode] = useState('');
  const [tls, setTls] = useState<FlnTlRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the scalar form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setSourceTable(row.source_table ?? '');
    setSourceColumn(row.source_column ?? '');
    setKindCode(row.source_kind_code);
    setError(null);
  }, [row]);

  // Load the existing descriptions (read-only display + status derivation). Keyed
  // by the row id so it refreshes when a different field is opened.
  useEffect(() => {
    if (!row) { setTls([]); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('fln_field_source_tl')
        .select('locale_code, description, derivation_notes')
        .eq('fln_field_source_id', row.fln_field_source_id);
      if (!cancelled) setTls((data ?? []) as FlnTlRow[]);
    })();
    return () => { cancelled = true; };
  }, [row]);

  const activeDescription = useMemo(() => {
    const exact = tls.find((r) => r.locale_code === activeLocale && (r.description ?? '').trim());
    const fb = tls.find((r) => r.locale_code === 'EN_US' && (r.description ?? '').trim());
    return (exact ?? fb)?.description ?? '';
  }, [tls, activeLocale]);

  const kindOptions = useMemo(
    () => Object.keys(kindLabels).map((code) => ({ code, label: kindLabels[code] })),
    [kindLabels],
  );

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    const tbl = sourceTable.trim() || null;
    const col = sourceColumn.trim() || null;
    const hasDesc = tls.some((r) => (r.description ?? '').trim());
    // Mirror the desktop status derivation.
    const status = tbl && col ? (hasDesc ? 'RESOLVED' : 'NEEDS_DESCRIPTION') : 'UNRESOLVED';
    // §2: route through the Common Routine. is_manual='Y' locks it against re-scan.
    const { error: e } = await routedUpdate(session, 'fln_field_source', 'fln_field_source_id', row.fln_field_source_id, {
      source_table: tbl,
      source_column: col,
      source_kind_code: kindCode,
      resolution_status_code: status,
      is_manual: 'Y',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.fln.edit_sheet', 'Map field source')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Database size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.fln.edit_title', 'Map field source')}</div>
                <div className="m-em">{row.field_key}</div>
              </span>
            </div>

            {row.tech_hint && (
              <div className="m-card">
                <div className="m-empty" style={{ textAlign: 'left' }}>
                  <strong>{t('mobile.fln.detected', 'Detected')}: </strong>{row.tech_hint}
                </div>
              </div>
            )}

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-fln-table">{t('mobile.fln.source_table', 'Source table')}</label>
                <input
                  id="m-fln-table"
                  className="m-inp"
                  value={sourceTable}
                  onChange={(e) => setSourceTable(e.target.value)}
                  placeholder="csm_user_master"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-fln-column">{t('mobile.fln.source_column', 'Source column')}</label>
                <input
                  id="m-fln-column"
                  className="m-inp"
                  value={sourceColumn}
                  onChange={(e) => setSourceColumn(e.target.value)}
                  placeholder="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-fln-kind">{t('mobile.fln.kind', 'Source kind')}</label>
                <select id="m-fln-kind" className="m-sel" value={kindCode} onChange={(e) => setKindCode(e.target.value)}>
                  {kindOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.fln.description', 'Description')}</label>
                <div className="m-formrow-ro">
                  {activeDescription || t('common.none', 'None')}
                  <span className="m-formrow-hint"> · {t('mobile.fln.desc_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
